/* eslint-disable @next/next/no-img-element */
import useCommon from '@utils/hooks/common';
import {Button, Drawer, DrawerProps, Spin} from 'antd';
import React, {CSSProperties, useEffect, useState} from 'react';
import classNames from 'classnames';
import AnimatedEmojiIcon from '@public/icon/game-room/animatedEmoji.svg';
import StaticEmojiIcon from '@public/icon/game-room/staticEmoji.svg';
import styles from './styles';
import EmojiPanel from './EmojiPanel';
import getImage from '../GameRoom/CanvasArea/helper';

interface EmojiDrawerProps extends DrawerProps {
  closeDrawer?: () => void;
  roomNumber?: number | string;
  emojiList: Array<{id: number; diamond: number; image: string}>;
  playerItemList: Array<{item: {}; itemId: number; numberOfItem: number; playerId: number}>;
  isEmojiListLoading?: boolean;
}

interface Emoji {
  id: number;
  diamond: number;
  image: string;
}

const EmojiDrawer = React.memo((props: EmojiDrawerProps) => {
  const {closeDrawer, roomNumber, open, emojiList, playerItemList, isEmojiListLoading} = props;
  const {classes, socket, t, theme} = useCommon({
    withStyles: styles,
    observers: ['socket', 'user'],
    withTranslation: ['emojiPanel'],
  });

  const [panelType, setPanelType] = useState<'STATIC' | 'ANIMATED'>('STATIC');
  const [staticEmojiList, setStaticEmojiList] = useState<Array<Emoji>>([]);
  const [animatedEmojiList, setAnimatedEmojiList] = useState<Array<Emoji>>([]);

  // should be sync with imageUrls in CanvasArea/ helper.ts; need manually update
  const animatedEmojiNames = ['jetFighter', 'waterBucket', 'bomb', 'poop', 'smoke'];

  useEffect(() => {
    if (emojiList) {
      // animatedEmojiPaths refers file path in server
      const animatedEmojiPaths = animatedEmojiNames.map(emojiName => getImage(emojiName));

      for (const imageUrl of animatedEmojiPaths) {
        let isAnimated = false;
        let animatedEmoji;
        for (const emoji of emojiList) {
          if (emoji.image === imageUrl) {
            if (!animatedEmojiList.some(emojiItem => emojiItem.image === imageUrl)) {
              isAnimated = true;
              animatedEmoji = emoji;
              break;
            }
          }
        }

        if (isAnimated && animatedEmoji) {
          setAnimatedEmojiList(prevList => [...prevList, animatedEmoji]);
        }
      }

      const staticEmojis = emojiList.filter(emoji => !animatedEmojiPaths.includes(emoji.image));
      setStaticEmojiList(staticEmojis);
    }
  }, [emojiList]);

  useEffect(() => {
    if (open) {
      setPanelType('STATIC');
    }
  }, [open]);

  const drawerBodyBackground =
    // eslint-disable-next-line max-len
    'linear-gradient(black 0%, black 33.33%, black 80%, rgba(0, 0, 0, 0) 100%), url(images-mobile/CustomizedModal/LowGround.webp)';

  const drawerHeaderStyle: CSSProperties = {
    height: 50,
    // background: 'black',
    // background: theme.backgroundColor,
    // backgroundImage: 'url("/images-mobile/RedeemDrawer/header-background.svg")',
    // border: 'none',
    background: '#202633',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    color: 'white',
    borderBottom: `1px ${theme.borderColor} solid`,
  };

  const drawerBodyStyle: CSSProperties = {
    // background: drawerBodyBackground,
    background: '#202633',
    padding: 0,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'bottom center',
    backgroundSize: 'cover',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };

  const drawerFooterStyle: CSSProperties = {
    height: 50,
    textAlign: 'start',
    borderTop: `1px ${theme.borderColor} solid`,
    background: '#202633',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100%',
    padding: 0,
  };

  const previousIcon = (): JSX.Element => (
    <img
      src="/images-mobile/RedeemDrawer/previous.svg"
      // srcSet="/images-mobile/CustomizedModal/Cross.webp"
      alt="modal_previous_icon"
      className={classes.previousIcon}
    />
  );

  const drawerTitle = (): JSX.Element => (
    <div className={classes.drawerTitleArea}>
      <div className={classes.drawerTitle}>{t('emoji')}</div>
      <span className={classes.closeIcon} onClick={() => closeDrawer()}>
        +
      </span>
    </div>
  );
  const footer = (
    <>
      {/* <img
        className={classes.dividerImage}
        src="/images-mobile/game-room/HistoryAndEmojiDrawer/goldDivider.webp"
        alt="dividerImage"
      /> */}
      <Button
        className={classNames(classes.footerButton, panelType === 'STATIC' && classes.isSelected)}
        icon={<StaticEmojiIcon className={classes.buttonIcon} />}
        onClick={() => {
          if (panelType !== 'STATIC') setPanelType('STATIC');
        }}
      />
      <Button
        className={classNames(classes.footerButton, panelType === 'ANIMATED' && classes.isSelected)}
        icon={<AnimatedEmojiIcon className={classes.buttonIcon} />}
        onClick={() => {
          if (panelType !== 'ANIMATED') setPanelType('ANIMATED');
        }}
      />
    </>
  );

  // if (!visible) return null;

  return (
    <Drawer
      title={drawerTitle()}
      open={open}
      closable
      closeIcon={previousIcon()}
      height={525}
      rootClassName={classes.historyDrawer}
      placement="bottom"
      width={500}
      onClose={() => closeDrawer()}
      styles={{
        header: drawerHeaderStyle,
        body: drawerBodyStyle,
        footer: drawerFooterStyle,
      }}
      footer={footer}
    >
      {/* <img
        src="/images-mobile/CustomizedModal/UpperPart_BG_Border.webp"
        alt=""
        className={classes.divider}
      /> */}
      <div className={classes.emojiPanelContainer}>
        {isEmojiListLoading ? <Spin className={classes.spinner} /> : null}
        <div className={classNames(panelType !== 'STATIC' && classes.hidden)}>
          {!staticEmojiList || staticEmojiList.length === 0 ? (
            <span className={classes.message}>{t('no_available_emoji')}</span>
          ) : (
            <EmojiPanel
              isEmojiPanelVisible={open}
              emojiList={staticEmojiList}
              playerItemList={playerItemList}
            />
          )}
        </div>
        <div className={classNames(panelType !== 'ANIMATED' && classes.hidden)}>
          {!animatedEmojiList || animatedEmojiList.length === 0 ? (
            <span className={classes.message}>{t('no_available_emoji')}</span>
          ) : (
            <EmojiPanel
              isEmojiPanelVisible={open}
              emojiList={animatedEmojiList}
              playerItemList={playerItemList}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
});

export default EmojiDrawer;
EmojiDrawer.displayName = 'EmojiDrawer';
